import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCw, ShieldAlert, ShieldCheck, ShieldQuestion, Loader2 } from "lucide-react";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";

/**
 * Dev view of the antivirus queue for files uploaded from outside GK ELEKTIM.
 *
 * A file awaiting a verdict cannot be downloaded, and without this page that state
 * is invisible — you would only notice it as a link that is not there. Anything
 * waiting longer than the stuck threshold is called out, because that means the
 * verdict is not coming on its own.
 */

interface ScanRow {
  id: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status: ScanStatus;
  created_at: string;
  scanned_at: string | null;
  waiting_minutes: number;
  is_stuck: boolean;
  uploaded_by_name: string | null;
  contractor_name: string | null;
}

interface Overview {
  enabled: boolean;
  stuck_after_minutes: number;
  counts: Record<ScanStatus, number>;
  stuck_count: number;
  pending: ScanRow[];
  recent: ScanRow[];
}

const STATUS_CONFIG: Record<
  ScanStatus,
  { label: string; color: "gray" | "green" | "red" | "yellow" | "blue" }
> = {
  not_required: { label: "Nie wymaga", color: "gray" },
  pending: { label: "W toku", color: "blue" },
  clean: { label: "Czysty", color: "green" },
  threat: { label: "Zagrożenie", color: "red" },
  unsupported: { label: "Nieobsługiwany", color: "yellow" },
  failed: { label: "Skan nieudany", color: "yellow" },
};

function formatWaiting(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${minutes % 60} min`;
  return `${Math.floor(hours / 24)} dni ${hours % 24} h`;
}

function formatSize(bytes: number | null) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileScansPage() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  usePageTitle("Skanowanie plików zewnętrznych");

  const { data, isLoading } = useQuery({
    queryKey: ["dev", "file-scans"],
    queryFn: () =>
      apiClient
        .get<{ data: Overview }>("/maintenance/file-scans/")
        .then((r) => r.data.data),
    // Verdicts land within seconds — keep the queue view live while it is open.
    refetchInterval: 15_000,
    enabled: !!user?.is_developer,
  });

  const recheck = useMutation({
    mutationFn: (id: string) => apiClient.post(`/maintenance/file-scans/${id}/recheck/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dev", "file-scans"] }),
  });

  if (!user?.is_developer) return <Navigate to="/" replace />;
  if (isLoading || !data) return <PageLoader />;

  const renderRows = (rows: ScanRow[], showWaiting: boolean) => (
    <tbody>
      {rows.map((row) => (
        <tr
          key={row.id}
          className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
        >
          <td className="px-4 py-2.5 text-[var(--color-text-primary)]">
            <span className="font-medium">{row.original_filename}</span>
            <span className="ml-2 text-[var(--color-text-muted)]">{formatSize(row.size_bytes)}</span>
          </td>
          <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
            {row.contractor_name ?? row.uploaded_by_name ?? "—"}
          </td>
          <td className="px-4 py-2.5">
            <div className="flex items-center gap-2">
              <Badge color={STATUS_CONFIG[row.scan_status].color}>
                {STATUS_CONFIG[row.scan_status].label}
              </Badge>
              {row.is_stuck && showWaiting && (
                <Badge color="red">
                  Zawieszony &gt; {Math.floor(data.stuck_after_minutes / 60) || 1} h
                </Badge>
              )}
            </div>
          </td>
          <td className="px-4 py-2.5 whitespace-nowrap text-[var(--color-text-secondary)]">
            {showWaiting
              ? formatWaiting(row.waiting_minutes)
              : row.scanned_at
                ? new Date(row.scanned_at).toLocaleString("pl")
                : "—"}
          </td>
          <td className="px-4 py-2.5 text-right">
            <Button
              variant="secondary"
              onClick={() => recheck.mutate(row.id)}
              disabled={recheck.isPending}
              className="!py-1 !px-2 !text-xs"
            >
              <RotateCw size={12} className="mr-1 inline" />
              Sprawdź
            </Button>
          </td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={5} className="px-4 py-6 text-center text-[var(--color-text-muted)]">
            Brak pozycji.
          </td>
        </tr>
      )}
    </tbody>
  );

  const header = (
    <tr className="text-left">
      <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Plik</th>
      <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Od kogo</th>
      <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Status</th>
      <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Czas</th>
      <th className="px-4 py-2" />
    </tr>
  );

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Skanowanie plików zewnętrznych
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
          Pliki wgrane przez konta podwykonawców. Do czasu czystego wyniku nie da się ich
          pobrać — wynik „w toku” dłuższy niż {data.stuck_after_minutes} min oznacza, że
          werdykt nie przyjdzie sam.
        </p>
      </div>

      {/* Stan bramki */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {data.enabled ? (
          <Badge color="green">
            <ShieldCheck size={12} className="mr-1 inline" /> Bramka włączona
          </Badge>
        ) : (
          <Badge color="gray">
            <ShieldQuestion size={12} className="mr-1 inline" /> Bramka wyłączona — pliki
            nie są blokowane
          </Badge>
        )}
        {data.stuck_count > 0 && (
          <Badge color="red">
            <ShieldAlert size={12} className="mr-1 inline" /> Zawieszone: {data.stuck_count}
          </Badge>
        )}
      </div>

      {/* Liczniki */}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {(Object.keys(STATUS_CONFIG) as ScanStatus[]).map((key) => (
          <div
            key={key}
            className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2"
          >
            <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              {STATUS_CONFIG[key].label}
            </p>
            <p className="text-xl font-semibold text-[var(--color-text-primary)]">
              {data.counts[key] ?? 0}
            </p>
          </div>
        ))}
      </div>

      <section className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 border-l-4 border-[var(--color-primary)] pl-3 text-base font-semibold text-gray-900 dark:text-gray-100">
          Aktualnie skanowane
          {data.pending.length > 0 && (
            <Loader2 size={14} className="animate-spin text-[var(--color-text-muted)]" />
          )}
        </h2>
        <div className="overflow-x-auto border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-bg-elevated)]">{header}</thead>
            {renderRows(data.pending, true)}
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-3 border-l-4 border-[var(--color-primary)] pl-3 text-base font-semibold text-gray-900 dark:text-gray-100">
          Ostatnie wyniki
        </h2>
        <div className="overflow-x-auto border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-bg-elevated)]">{header}</thead>
            {renderRows(data.recent, false)}
          </table>
        </div>
      </section>
    </div>
  );
}
